// @ts-strict-ignore
import React, { memo, useCallback, useState } from 'react'
import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ResponsiveDialog from 'components/ResponsiveDialog'
import Button from 'components/Button'
import AddIcon from '@mui/icons-material/Add'
import { useRouter } from 'next/router'
import { PreloadedQuery, usePreloadedQuery } from 'react-relay'
import { graphql } from 'relay-runtime'
import { useNodes } from 'hooks/relay'
import { UseQuestionDialog_Query } from '__generated__/UseQuestionDialog_Query.graphql'

export const UseQuestionDialogQuery = graphql`
  query UseQuestionDialog_Query($questionId: ID!) {
    question: node(id: $questionId) {
      ... on Question {
        title
        description
      }
    }

    currentUser {
      # TODO: pagination?
      allJobs(first: 20) {
        nodes {
          slug
          title
        }
      }
    }
  }
`

interface IUseQuestionDialogProps {
  preloadedQuery: PreloadedQuery<UseQuestionDialog_Query>
  open: boolean
  onClose: () => void
}

function UseQuestionDialog({ preloadedQuery, open, onClose }: IUseQuestionDialogProps) {
  const [selectedJobSlug, setSelectedJobSlug] = useState<string>(null)
  const router = useRouter()
  const data = usePreloadedQuery(UseQuestionDialogQuery, preloadedQuery)
  const theme = useTheme()
  const question = data?.question
  const jobs = useNodes(data?.currentUser?.allJobs?.nodes)

  const onJobChange = useCallback((event) => {
    setSelectedJobSlug(event.target.value)
  }, [selectedJobSlug])

  const addQuestionToJob = useCallback(() => {
    if (selectedJobSlug) {
      router.push(`/hire/jobs/${selectedJobSlug}/details?question_title=${question.title}`)
    } else {
      router.push(`/hire/jobs/new/details?question_title=${question.title}`)
    }
  }, [router, selectedJobSlug, question?.title])

  if (!question) return null

  return (
    <ResponsiveDialog open={open} onClose={onClose}>
      <DialogTitle>{question.title}</DialogTitle>

      {question.description && (
      <DialogContent>
        <DialogContentText>{question.description}</DialogContentText>
      </DialogContent>
      )}

      <DialogContent>
        <DialogContentText>
          Choose which Job you want to add the Question to, or add it to a New Job.
        </DialogContentText>
      </DialogContent>

      <DialogContent>
        <Select
          value={selectedJobSlug || 0}
          onChange={onJobChange}
          fullWidth
          data-cy="select-job"
        >
          <MenuItem value={0} key={0} data-cy="select-job-0">New Job</MenuItem>
          {jobs.map(j => (
            <MenuItem value={j.slug} key={j.slug} data-cy={`select-job-${j.slug}`}>{j.title}</MenuItem>
          ))}
        </Select>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} data-cy="close-dialog">Close</Button>
        <Button
          color="primary"
          onClick={addQuestionToJob}
          data-cy="submit-dialog"
          startIcon={<AddIcon />}
        >
          Add
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}

export default memo(UseQuestionDialog)
